import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import i18n from "../i18n";

export type AppLang = "en" | "ta";

type LanguageContextValue = {
  lang: AppLang;
  setLang: (l: AppLang) => void;
  toggleLang: () => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_KEY = "tkm_lang";

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<AppLang>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ta" || saved === "en") return saved;
    const current = (i18n.language || "en").slice(0, 2) as AppLang;
    return current === "ta" ? "ta" : "en";
  });

  const setLang = (l: AppLang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
    i18n.changeLanguage(l);
    document.documentElement.lang = l === "ta" ? "ta" : "en";
  };

  const toggleLang = () => setLang(lang === "ta" ? "en" : "ta");

  useEffect(() => {
    // Ensure i18n is in sync on first mount
    const wanted = lang;
    const current = (i18n.language || "en").slice(0, 2);
    if (current !== wanted) i18n.changeLanguage(wanted);
    document.documentElement.lang = wanted === "ta" ? "ta" : "en";
  }, []);

  const value = useMemo(() => ({ lang, setLang, toggleLang }), [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
};
